import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {Link as RouterLink, generatePath, useParams} from 'react-router-dom';

import {skipToken} from '@reduxjs/toolkit/query';
import saveAs from 'file-saver';

import {Button, NewSelectField, showModal, showNewNotification} from '@edna/components';
import {
  Modal,
  ModalActions,
  ModalCancelButton,
  ModalHeader,
  ModalSubmitButton,
} from '@edna/components/Modals';
import {Stack} from '@edna/components/primitives';
import {ECurrency, ECurrencySymbol} from '@edna/utils';

import {Message} from 'src/components';
import {SecondaryLink, SecondaryText} from 'src/components/primitives';
import {EPaymentTab} from 'src/containers/Licenses/PaymentTab/definitions';
import {useLazyGetFileQuery} from 'src/containers/ServerFile/api';
import {getNameByUrl} from 'src/containers/ServerFile/utils';
import {useGetSupportClientQuery} from 'src/containers/SupportClients/api';
import {EResellerIds, useGetBankDetailsQuery, useGetTenantDataQuery} from 'src/containers/User';
import {DocumentationIcon} from 'src/icons';
import {MagicianImage} from 'src/images';
import PATHS from 'src/paths';
import {joiResolver} from 'src/utils/validator';

import {useGeneratePaymentLinkMutation} from '../api';
import * as S from '../style';

type TCloseModalProps = {
  closeModal: TEmptyFunction;
};

type TInvoiceGeneratedModal = {
  closeModal: TEmptyFunction;
  queryFileName: string;
};

const InvoiceGeneratedModal = React.memo<TInvoiceGeneratedModal>(({queryFileName}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const [getFile] = useLazyGetFileQuery();

  const handleDownload = React.useCallback(async () => {
    try {
      const file = await getFile({id: queryFileName}).unwrap();

      saveAs(file);

      showNewNotification({
        type: 'SUCCESS',
        message: t('Common:successDownload'),
      });
    } catch {}
  }, [dispatch, queryFileName, t]);

  return (
    <Modal width="435px">
      <ModalHeader>{t('BalanceReplenishment:invoiceGeneratedModal.title')}</ModalHeader>
      <SecondaryText size="S">
        <Trans i18nKey="BalanceReplenishment:invoiceGeneratedModal.content">
          <SecondaryLink
            to={generatePath(PATHS.COMPANY_PROFILE_PAYMENT, {
              paymentTab: EPaymentTab.DOCUMENTATION,
            })}
            as={RouterLink}
          />
        </Trans>
      </SecondaryText>
      <S.ModalCenterImage as={MagicianImage} />
      <S.CenterModalActions>
        <ModalSubmitButton onClick={handleDownload}>
          <S.DownloadIcon />
          {t('BalanceReplenishment:invoiceGeneratedModal.download')}
        </ModalSubmitButton>
      </S.CenterModalActions>
    </Modal>
  );
});

type TInvoicePaymentForm = {
  paymentBankId: number;
  amount: number;
};

const createInvoiceInitialValues = {
  paymentBankId: 0,
  amount: 0,
};

const MAX_AMOUNT_LENGTH = 14;

const InvoiceModal = React.memo<TCloseModalProps>(({closeModal}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {id: supportTenantId} = useParams<'id'>();

  const {data: supportClient} = useGetSupportClientQuery(supportTenantId ?? skipToken);
  const {data: tenantData} = useGetTenantDataQuery();

  const defaultCurrency = tenantData?.defaultCurrency ?? ECurrency.RUB;
  const [generatePaymentLink, {isLoading: isGeneratePaymentLinkLoading}] =
    useGeneratePaymentLinkMutation();
  const paymentType = tenantData?.paymentType;

  const isSupport = !!supportTenantId;
  const currencyCode = isSupport ? supportClient?.defaultCurrency : defaultCurrency;

  const {data: bankDetails = [], isLoading: isBankDetailsLoading} = useGetBankDetailsQuery(
    {
      currencyCode,
      resellerId: tenantData?.resellerId as EResellerIds,
      paymentType,
    },
    {skip: !tenantData || !paymentType},
  );

  const createInvoiceValidate = joiResolver((joi) => {
    if (bankDetails.length > 1) {
      return joi.object({
        paymentBankId: joi.number().required(),
        amount: joi.number().required(),
      });
    }

    return joi.object({
      amount: joi.number().required(),
    });
  });

  const methods = useForm<TInvoicePaymentForm>({
    defaultValues: createInvoiceInitialValues,
    resolver: createInvoiceValidate,
  });

  const onSubmit = React.useCallback(
    async (values: TInvoicePaymentForm) => {
      const bankInfo = bankDetails.find((item) => item.id === values.paymentBankId);

      const result = await generatePaymentLink({
        tenantId: supportTenantId,
        bankDetails: bankInfo,
        amount: values.amount,
      })
        .unwrap()
        .catch(() => {});

      if (result) {
        closeModal();
        const queryFileName = getNameByUrl(result.downloadLink);

        if (queryFileName) {
          showModal((closeInvoiceGenerated) => (
            <InvoiceGeneratedModal
              queryFileName={queryFileName}
              closeModal={closeInvoiceGenerated}
            />
          ));
        } else {
          showNewNotification({
            type: 'ERROR',
            messageKey: 'Common:errorDownload',
          });
        }
      }
    },
    [closeModal, dispatch, bankDetails, supportTenantId],
  );

  if (isBankDetailsLoading) {
    return null;
  }

  return (
    <Modal>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <ModalHeader>{t('BalanceReplenishment:invoiceSettingsModal.title')}</ModalHeader>
          <SecondaryText size="S">
            {t('BalanceReplenishment:invoiceSettingsModal.amount')}
          </SecondaryText>
          <S.Row>
            {bankDetails.length > 1 && (
              <NewSelectField
                name="paymentBankId"
                label={t('LeadCompanyClientEdit:labels.bank')}
                options={bankDetails.map((item) => ({
                  label: item.beneficiaryBank,
                  value: item.id,
                }))}
              />
            )}
            <Stack size="2" direction="ROW">
              <S.NumberInputField
                label={t('BalanceReplenishment:invoiceSettingsModal.insertSum')}
                name="amount"
                isDecimal
                maxLength={MAX_AMOUNT_LENGTH}
              />
              {!!currencyCode && <S.Currency hasMargin>{ECurrencySymbol[currencyCode]}</S.Currency>}
            </Stack>
          </S.Row>
          <Message
            type="Warning"
            shadowSize="XS"
            noMargin
            title={t('BalanceReplenishment:invoiceSettingsModal.warning')}
          />
          <ModalActions>
            <ModalCancelButton data-selector="ModalCancelButton" onClick={closeModal}>
              {t('Common:button.cancel')}
            </ModalCancelButton>
            <ModalSubmitButton
              loading={isGeneratePaymentLinkLoading}
              disabled={isGeneratePaymentLinkLoading}
            >
              {t('BalanceReplenishment:invoiceSettingsModal.generateButton')}
            </ModalSubmitButton>
          </ModalActions>
        </form>
      </FormProvider>
    </Modal>
  );
});

InvoiceModal.displayName = 'InvoiceModal';

const showInvoiceModal = () => showModal((closeModal) => <InvoiceModal closeModal={closeModal} />);

const InvoiceButton = React.memo(() => {
  const {t} = useTranslation();

  return (
    <Button onClick={showInvoiceModal} size="S">
      <S.ButtonLeftIcon as={DocumentationIcon} />
      {t('BalanceReplenishment:actions.generateInvoice')}
    </Button>
  );
});

export {InvoiceButton, showInvoiceModal};
